'use client';

import React from 'react';
import { useLeadGenerationInteraction } from 'components/analytics/hooks/useLeadGenerationInteraction';
import { LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { getCookieValue, convertObjectToArray } from 'components/analytics/utils';
import { NINETAILED_ANONYMOUS_ID_COOKIE } from 'utils/cookies';
import { trackNinetailedExperienceViewed } from 'components/analytics/eventHandlerTracking';
import {
  AnalyticsModuleTrackingProvider,
  useAnalyticsModuleTracking,
} from 'components/analytics/AnalyticsModuleTracking';
import type { LeadGenerationInteractionProps } from 'components/analytics/types';
import { ExperienceType } from '@ninetailed/experience.js-shared';

interface NinetailedExperienceTrackerProps {
  experienceName: string;
  experienceSnowflakeName: string | null;
  experienceId: string;
  variantName: string;
  variantId: string;
  variantIndex: number;
  experienceType?: ExperienceType | undefined;
}

const VARIANT_NAMES = {
  BASELINE: 'Baseline',
  CONTROL: 'Control',
  VARIANT_PREFIX: 'Variant',
};

const EXPERIENCE_TYPES = {
  EXPERIMENT: 'nt_experiment',
  PERSONALIZATION: 'nt_personalization',
};

/**
 * Determines the appropriate variant name based on experience type and variant data
 * @param variantIndex - The numerical index of the variant
 * @param variantName - The Internal Name of the variant from Contentful
 * @param experienceType - The type of Ninetailed experience (nt_personalization or nt_experiment)
 * @returns The name for the variant for Snowflake
 */
const getVariantDisplayName = (
  variantIndex: number,
  variantName: string,
  experienceType?: ExperienceType | undefined,
): string => {
  // For personalizations, use the entry's Internal Name
  if (experienceType === EXPERIENCE_TYPES.PERSONALIZATION) {
    return variantName;
  }

  // For experiments, use a standardized name (Control for index 0, Variant 1, Variant 2, etc)
  if (experienceType === EXPERIENCE_TYPES.EXPERIMENT) {
    if (variantIndex === 0) {
      return VARIANT_NAMES.CONTROL;
    }
    return `${VARIANT_NAMES.VARIANT_PREFIX} ${variantIndex}`;
  }

  // If there is no experience type, we are showing the baseline entry
  return VARIANT_NAMES.BASELINE;
};

const NinetailedExperienceTrackerInner: React.FC<NinetailedExperienceTrackerProps> = ({
  experienceName,
  experienceSnowflakeName,
  experienceId,
  variantName,
  variantId,
  variantIndex,
  experienceType,
}) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const experienceData = React.useMemo(
    () => ({
      ninetailed_id: getCookieValue(NINETAILED_ANONYMOUS_ID_COOKIE) ?? '', // Ninetailed visitor anonymous ID
      experience_name: experienceSnowflakeName || experienceName, // Name for Ninetailed Experience
      experience_id: experienceId, // Entry ID for Ninetailed Experience
      variant_name: getVariantDisplayName(variantIndex, variantName, experienceType), // Internal Name or standardized variant name
      variant_id: variantId, // Entry ID for variant entry
      experience_type: experienceType, // Type of experience
    }),
    [experienceName, experienceSnowflakeName, experienceId, variantName, variantId, variantIndex, experienceType],
  );

  const analyticsProps: LeadGenerationInteractionProps = {
    interaction_type: LEAD_GENERATION_INTERACTION_TYPES.NINETAILED_EXPERIENCE_VIEWED,
    interaction_details: {
      location: attributes.moduleLocation,
      action_detail: 'experience_viewed',
    },
    additional_data: convertObjectToArray(experienceData),
  };

  const trackEvent = useLeadGenerationInteraction(analyticsProps);

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();
      trackNinetailedExperienceViewed(experienceData);
      hasTracked.current = true;
    }
  }, [experienceData, trackEvent]);

  return null;
};

export const NinetailedExperienceTracker: React.FC<NinetailedExperienceTrackerProps> = props => {
  return (
    <AnalyticsModuleTrackingProvider>
      <NinetailedExperienceTrackerInner {...props} />
    </AnalyticsModuleTrackingProvider>
  );
};
